import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContentContext";
import { Reveal, SectionTag } from "./Reveal";
import { sectorIcons, serviceIcons } from "@/i18n/content";
import { fetchCases, toAbsolute } from "@/lib/api";

// Sector index → service indices (best-fit mapping). Kept static since sectors are stable.
// Service order: 0 hotel, 1 erp, 2 scada, 3 security, 4 web, 5 mobile, 6 AI, 7 hosting.
const SECTOR_SERVICE_MAP = [
  [0, 4, 5],       // 0 Hotels → hotel, web, mobile
  [1, 5, 4],       // 1 Restaurants → erp, mobile, web
  [1, 4, 5],       // 2 Retail → erp, web, mobile
  [1, 6, 7],       // 3 Distribution → erp, AI, hosting
  [3, 6, 7],       // 4 Logistics → security, AI, hosting
  [1, 4, 5],       // 5 Education → erp, web, mobile
  [3, 1, 4],       // 6 Healthcare → security, erp, web
  [1, 4, 6],       // 7 Government → erp, web, AI
  [3, 5, 6],       // 8 Transport → security, mobile, AI
  [2, 3, 6],       // 9 Oil & Energy → SCADA, security, AI
];

export default function SectorExplorer() {
  const { t, lang } = useLang();
  const { settings } = useSite();
  const [active, setActive] = useState(0);
  const [cases, setCases] = useState([]);

  useEffect(() => {
    fetchCases(lang).then((r) => setCases(r.data || [])).catch(() => setCases([]));
  }, [lang]);

  const sectors = t.sectors.items.map((s, i) => settings[`sector_${i}_${lang}`] || s);
  const serviceItems = t.services.items;
  const activeServices = (SECTOR_SERVICE_MAP[active] || []).map((si) => ({
    idx: si,
    t: settings[`service_${si}_title_${lang}`] || serviceItems[si]?.t,
    d: settings[`service_${si}_desc_${lang}`] || serviceItems[si]?.d,
  }));
  const activeCases = cases.slice(0, 3); // Fallback: first 3 cases (until per-sector tagging is added)

  return (
    <section id="sector-explorer" className="relative section-pad" data-testid="sector-explorer-section">
      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 70% 40%, rgba(10,30,78,0.4), transparent 55%)" }} />
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <div className="text-center mb-10">
          <Reveal><SectionTag>{t.sectorExplorer.tag}</SectionTag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5 flex items-center justify-center gap-3">
              <Sparkles size={32} className="text-primary" /> {t.sectorExplorer.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-slate-400 text-base sm:text-lg mt-4 max-w-2xl mx-auto">{t.sectorExplorer.subtitle}</p>
          </Reveal>
        </div>

        {/* Sector selector */}
        <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="sector-selector">
          {sectors.map((s, i) => {
            const Icon = sectorIcons[i % sectorIcons.length];
            const isActive = active === i;
            return (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold border transition-all ${isActive ? "text-[#04120a] border-transparent" : "border-white/10 hover:border-[color:var(--primary)] text-slate-300"}`}
                style={isActive ? { background: "var(--primary)" } : {}}
                data-testid={`sector-btn-${i}`}
              >
                <Icon size={16} />
                {s}
              </button>
            );
          })}
        </div>

        {/* Recommended services for this sector */}
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="grid lg:grid-cols-3 gap-5"
          data-testid="sector-services"
        >
          {activeServices.map((sv, i) => {
            const Icon = serviceIcons[sv.idx] || serviceIcons[0];
            return (
              <div key={i} className="card-surface rounded-2xl p-6 group hover:border-[color:var(--primary)] transition-colors" data-testid={`sector-service-${i}`}>
                <div className="h-12 w-12 rounded-xl grid place-items-center mb-4" style={{ background: "rgba(0,242,96,0.15)" }}>
                  <Icon size={22} className="text-primary" />
                </div>
                <h3 className="font-extrabold text-lg mb-2">{sv.t}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{sv.d}</p>
              </div>
            );
          })}
        </motion.div>

        {activeCases.length > 0 && (
          <div className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-extrabold text-lg">{t.sectorExplorer.relatedCases}</h3>
              <Link to="/case-studies" className="text-sm text-primary font-bold inline-flex items-center gap-1 hover:underline">
                {t.sectorExplorer.viewAll} <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {activeCases.map((c) => (
                <Link key={c.id} to={`/case-studies/${c.slug || c.id}`} className="group card-surface rounded-2xl overflow-hidden block" data-testid={`sector-case-${c.id}`}>
                  <div className="aspect-video overflow-hidden">
                    <img src={toAbsolute(c.cover_image)} alt={c.title} loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                  </div>
                  <div className="p-4">
                    <div className="text-xs text-primary font-bold mb-1">{c.sector}</div>
                    <div className="font-bold text-sm line-clamp-2">{c.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
